/**
 * Carga de Google Identity Services (GIS).
 *
 * El SSO de Google Workspace es la unica puerta de entrada de la UI. GIS nos
 * entrega un **ID token** firmado por Google; la UI no lo interpreta, solo lo
 * manda en `Authorization: Bearer <id_token>` y es la API la que lo verifica
 * contra Google y decide el rol. Aca no hay ninguna decision de seguridad.
 *
 * El script se carga bajo demanda (no en el `index.html`) para que la pantalla
 * de login se pinte incluso si Google esta lento o inalcanzable, y para que los
 * tests nunca vayan a la red.
 */

const SCRIPT_ID = 'google-identity-services';
const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

/** Respuesta del boton de Google: `credential` es el ID token (un JWT). */
export interface GoogleCredentialResponse {
  readonly credential?: string;
  readonly select_by?: string;
}

interface GoogleIdConfiguration {
  readonly client_id: string;
  readonly callback: (response: GoogleCredentialResponse) => void;
  /** Restringe el selector de cuentas al dominio de Workspace. */
  readonly hosted_domain?: string;
  readonly auto_select?: boolean;
  readonly cancel_on_tap_outside?: boolean;
  readonly ux_mode?: 'popup' | 'redirect';
}

interface GoogleButtonConfiguration {
  readonly type?: 'standard' | 'icon';
  readonly theme?: 'outline' | 'filled_blue' | 'filled_black';
  readonly size?: 'small' | 'medium' | 'large';
  readonly text?: 'signin_with' | 'signup_with' | 'continue_with';
  readonly shape?: 'rectangular' | 'pill';
  readonly width?: number;
  readonly locale?: string;
}

/** Subconjunto de `google.accounts.id` que usa esta UI. */
export interface GoogleIdentityApi {
  initialize(configuration: GoogleIdConfiguration): void;
  renderButton(parent: HTMLElement, configuration: GoogleButtonConfiguration): void;
  disableAutoSelect(): void;
}

declare global {
  interface Window {
    readonly google?: {
      readonly accounts?: {
        readonly id?: GoogleIdentityApi;
      };
    };
  }
}

/** Error de carga del script de Google, con mensaje para el usuario. */
export class GoogleIdentityUnavailableError extends Error {
  constructor(detail: string) {
    super(`No se pudo cargar el inicio de sesión de Google: ${detail}`);
    this.name = 'GoogleIdentityUnavailableError';
  }
}

let pending: Promise<GoogleIdentityApi> | null = null;

function resolveApi(): GoogleIdentityApi | null {
  return window.google?.accounts?.id ?? null;
}

/**
 * Inserta el script de GIS una sola vez y resuelve con su API.
 *
 * El resultado se memoiza: dos pantallas que lo pidan comparten la misma carga.
 * Si falla, se olvida la promesa para que un reintento del usuario vuelva a
 * intentarlo de verdad.
 */
export function loadGoogleIdentity(): Promise<GoogleIdentityApi> {
  const ready = resolveApi();
  if (ready !== null) {
    return Promise.resolve(ready);
  }
  if (pending !== null) {
    return pending;
  }

  pending = new Promise<GoogleIdentityApi>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    const script =
      existing instanceof HTMLScriptElement ? existing : document.createElement('script');

    const settle = (): void => {
      const api = resolveApi();
      if (api === null) {
        reject(new GoogleIdentityUnavailableError('el script cargo pero no expuso la API'));
        return;
      }
      resolve(api);
    };

    script.addEventListener('load', settle, { once: true });
    script.addEventListener(
      'error',
      () => {
        reject(new GoogleIdentityUnavailableError('la red o una extension bloqueo la descarga'));
      },
      { once: true },
    );

    if (!script.isConnected) {
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  pending = pending.catch((error: unknown) => {
    // Se olvida la promesa fallida para permitir reintentar; el error se propaga.
    pending = null;
    throw error;
  });

  return pending;
}

/** Cierra la sesion del lado de Google para que no re-seleccione la cuenta sola. */
export function disableGoogleAutoSelect(): void {
  resolveApi()?.disableAutoSelect();
}
